import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { LayoutGrid, List as ListIcon, SlidersHorizontal, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetTrigger, SheetContent } from "@/components/ui/sheet";
import { SpaCardGrid, SpaCardList } from "@/components/spa/SpaCard";
import FiltersSidebar from "@/components/filters/FiltersSidebar";
import EmptyState from "@/components/states/EmptyState";
import { SpaGridSkeleton } from "@/components/states/LoadingSkeletons";
import api from "@/lib/api";

const PAGE_SIZE = 12;

/** Convert the in-memory filters object into a URLSearchParams instance. */
function filtersToParams(filters, sort, page) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, String(v)); });
  params.set("sort", sort);
  params.set("page", String(page));
  params.set("page_size", String(PAGE_SIZE));
  return params;
}

export default function Listings() {
  const [sp, setSp] = useSearchParams();
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState(sp.get("sort") || "featured");
  const [page, setPage] = useState(parseInt(sp.get("page") || "1", 10));
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);

  const filters = useMemo(() => ({
    locality: sp.get("locality") || "",
    category: sp.get("category") || "",
    min_rating: sp.get("min_rating") || "",
    price: sp.get("price") || "",
    featured: sp.get("featured") === "true",
    verified: sp.get("verified") === "true",
    q: sp.get("q") || "",
  }), [sp]);

  useEffect(() => {
    document.title = "Browse Spas in Pune & PCMC - Pune Spa Guide";
  }, []);

  const fetchListings = useCallback(async () => {
    setItems(null);
    try {
      const params = filtersToParams(filters, sort, page);
      const r = await api.get(`/spas?${params.toString()}`);
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch {
      setItems([]);
    }
  }, [filters, sort, page]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const updateFilters = useCallback((next) => {
    const params = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => { if (v) params.set(k, String(v)); });
    if (sort !== "featured") params.set("sort", sort);
    setPage(1);
    setSp(params);
  }, [sort, setSp]);

  const clearFilters = useCallback(() => {
    setPage(1);
    setSp(new URLSearchParams());
  }, [setSp]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="spas-listings-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Directory</div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#111827]">Browse Spas</h1>
          <p className="text-sm text-[#4B5563] mt-1">{total} spa{total === 1 ? "" : "s"} found across Pune & PCMC</p>
        </div>
        <div className="flex items-center gap-2">
          {/* mobile filters */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" data-testid="filters-open-button" className="gap-2">
                  <SlidersHorizontal size={14} /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <FiltersSidebar filters={filters} onChange={updateFilters} onClear={clearFilters} />
              </SheetContent>
            </Sheet>
          </div>
          <div className="relative">
            <select
              data-testid="listings-sort-select"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="w-44 h-10 rounded-xl border border-[rgba(17,24,39,0.14)] bg-white px-3 pr-9 text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C9A86A]"
            >
              <option value="featured">Featured first</option>
              <option value="rating">Top Rated</option>
              <option value="newest">Newest</option>
              <option value="name">Name A-Z</option>
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563] pointer-events-none" />
          </div>
          <div className="hidden sm:flex bg-[#F5EFE6] rounded-xl p-1 gap-1" data-testid="listings-view-toggle">
            <button onClick={() => setView("grid")} className={`px-2 py-1.5 rounded-lg ${view === "grid" ? "bg-white shadow-sm" : ""}`}><LayoutGrid size={16} /></button>
            <button onClick={() => setView("list")} className={`px-2 py-1.5 rounded-lg ${view === "list" ? "bg-white shadow-sm" : ""}`}><ListIcon size={16} /></button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-20 rounded-2xl bg-white border border-[#E7DDCF] p-5">
            <FiltersSidebar filters={filters} onChange={updateFilters} onClear={clearFilters} />
          </div>
        </aside>
        <div className="lg:col-span-9">
          {items === null ? <SpaGridSkeleton count={6} /> : items.length === 0 ? (
            <EmptyState
              title="No spas match your filters"
              description="Try removing some filters or browse our top-rated spas."
              actionLabel="Clear filters"
              onAction={clearFilters}
            />
          ) : view === "grid" ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {items.map((s) => <SpaCardGrid key={s.id} spa={s} />)}
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((s) => <SpaCardList key={s.id} spa={s} />)}
            </div>
          )}

          {/* pagination */}
          {totalPages > 1 && (
            <div data-testid="listings-pagination" className="flex items-center justify-center gap-2 mt-10">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev"><ChevronLeft size={14}/> Prev</Button>
              <div className="text-sm px-3">Page {page} of {totalPages}</div>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="pagination-next">Next <ChevronRight size={14}/></Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
